import React, { memo, useCallback, useRef, useState } from 'react';
import cn from 'classnames';

import AcceptDeclineIconButtons from '@components/common/AcceptDeclineIconButtons/AcceptDeclineIconButtons';
import DynamicIconWrapper from '@components/common/DynamicIconWrapper';

import { Icons } from '@icons/icons';

import { TEmptyObject } from '@interfaces/common/common';
import { IBasicNode } from '@interfaces/components/tree';

import { ARRAY_NODE_KEY } from '../../constants';

import { IFlatTreeNodeValidation } from '../../interface';
import FlatTreeInput from '../FlatTreeInput';
import FlatTreeNodeError from '../FlatTreeNodeError';
import FlatTreeSelect from '../FlatTreeSelect';

import styles from './EditFlatNodeForm.module.scss';

interface IEditFlatNodeFormProps {
	node: IBasicNode;
	onSubmit: (idPath: string, value: string) => void;
	nodeValidation?: TEmptyObject | IFlatTreeNodeValidation;
	exitEditMode: () => void;
	className?: string;
}

const EditFlatNodeForm = ({
	node,
	nodeValidation = {},
	className,
	onSubmit,
	exitEditMode,
}: IEditFlatNodeFormProps) => {
	const [isError, setIsError] = useState(false);
	const [inputValue, setInputValue] = useState<string | undefined>(node?.value || '');

	const containerClassName = cn(styles.wrapper, className);

	const isErrorVisible = isError && nodeValidation?.errorText;

	const selectRef = useRef<HTMLSelectElement>(null);

	const {
		validateValue,
		validateInput,
		availableValues,
		requirements,
		errorText,
	} = nodeValidation;

	const isSelectEdit = availableValues && availableValues.length > 0;

	const editHandler = useCallback(() => {
		const value = isSelectEdit ? selectRef.current?.value : inputValue;

		if (value) {
			if (validateValue && !validateValue(value)) {
				setIsError(true);

				return;
			}

			exitEditMode();
			onSubmit(node.id, value);
			setIsError(false);
		} else if (validateValue) {
			setIsError(true);
		}
	}, [
		exitEditMode,
		node.id,
		onSubmit,
		validateValue,
		isSelectEdit,
		inputValue,
	]);

	return (
		<div className={containerClassName}>
			<div
				className={styles.editWrapper}
				data-testid='edit_node'
			>
				{node.key !== ARRAY_NODE_KEY && <span className={styles.key}>{node.key}:</span>}
				{isErrorVisible && <FlatTreeNodeError errorText={errorText} />}
				{requirements &&
					<span className={styles.requirements}>
						<DynamicIconWrapper
							icon={Icons.info}
							tooltipLabel={requirements}
						/>
					</span>
				}
				{isSelectEdit
					? <FlatTreeSelect
						defaultValue={node.value}
						options={availableValues}
						selectRef={selectRef}
					/>
					: <FlatTreeInput
						inputValue={inputValue}
						setInputValue={setInputValue}
						testName='edit_node_value'
						validateInput={validateInput}
					/>
				}
				<AcceptDeclineIconButtons
					negativeHandler={exitEditMode}
					positiveHandler={editHandler}
				/>
			</div>
		</div>
	);
};

export default memo(EditFlatNodeForm);
